import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Modal } from 'react-bootstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';

import { areYouSure } from 'lib/utils/toast';
import { GLOBAL_PRODUCT_TYPE } from 'lib/enums/ProductType';
import { GLOBAL_PRODUCT_VENDOR_CLONE_IMAGES } from 'lib/networking/endpoints';
import usePost from 'lib/hooks/usePost';

function CloneImagesModal({
  show,
  onHide,
  sourceProductOption,
  selectedProductOptions,
  selectedImageIds = [],
  refetch,
}) {
  const [targetProductOption, setTargetProductOption] = useState(null);

  // Filter out the source product and global product from the target options
  const targetOptions = selectedProductOptions.filter(
    option =>
      option.value !== sourceProductOption?.value && option.productType !== GLOBAL_PRODUCT_TYPE,
  );

  const hasSelectedImages = selectedImageIds.length > 0;

  const { postData: cloneImages, loading } = usePost(
    GLOBAL_PRODUCT_VENDOR_CLONE_IMAGES,
    () => {
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `${hasSelectedImages ? selectedImageIds.length : 'All'} image(s) cloned successfully`,
        timer: 1500,
      }).then(() => {
        setTargetProductOption(null);
        onHide();
        refetch();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred while cloning images: ${error?.response?.data?.message}`,
      }),
  );

  const handleClone = () => {
    const confirmMessage = hasSelectedImages
      ? `Do you want to clone ${selectedImageIds.length} image(s) to the selected product?`
      : 'Do you want to clone all images to the selected product?';

    areYouSure(
      () =>
        cloneImages({
          source_product_id: sourceProductOption.productId,
          source_product_type: sourceProductOption.productType,
          target_product_id: targetProductOption.productId,
          target_product_type: targetProductOption.productType,
          image_ids: hasSelectedImages ? selectedImageIds : null,
        }),
      confirmMessage,
      'Clone Images',
    );
  };

  const handleClose = () => {
    setTargetProductOption(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Clone Images To</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {hasSelectedImages ? (
          <p>
            <strong>{selectedImageIds.length}</strong> selected image(s) will be cloned to the
            target product.
          </p>
        ) : (
          <p>All images from the current product will be cloned to the selected target product.</p>
        )}
        <p className="font-weight-bold">Select target product:</p>
        <Select
          className="w-100"
          value={targetProductOption}
          options={targetOptions}
          onChange={option => setTargetProductOption(option)}
          placeholder="Select a product..."
          isDisabled={loading}
          isClearable
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleClone} disabled={!targetProductOption || loading}>
          {loading
            ? 'Cloning...'
            : `Clone ${hasSelectedImages ? selectedImageIds.length : 'All'} Image(s)`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

CloneImagesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  sourceProductOption: PropTypes.object,
  selectedProductOptions: PropTypes.array.isRequired,
  selectedImageIds: PropTypes.array,
  refetch: PropTypes.func,
};

export default CloneImagesModal;
